import { Helmet } from "react-helmet-async";
import { BUSINESS_INFO } from "@/lib/constants";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  noindex?: boolean;
}

/**
 * Normalizes a canonical URL to ensure consistency:
 * - Removes trailing slashes (except for root)
 * - Removes query parameters
 * - Removes hash fragments
 * - Ensures https://groomieroomie.com prefix
 */
const normalizeCanonical = (url: string): string => {
  const siteUrl = BUSINESS_INFO.website.url;
  
  // If it's a relative path, make it absolute
  let normalized = url.startsWith('http') ? url : `${siteUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  
  // Remove query parameters and hash
  normalized = normalized.split('?')[0].split('#')[0];
  
  // Remove trailing slash (except for root)
  if (normalized !== siteUrl && normalized !== `${siteUrl}/`) {
    normalized = normalized.replace(/\/$/, '');
  }
  
  // Ensure root URL doesn't have trailing slash issues
  if (normalized === `${siteUrl}/`) {
    normalized = siteUrl;
  }
  
  return normalized;
};

const SEOHead = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = "https://www.groomieroomie.com/og-image.jpg",
  ogType = "website",
  author,
  publishedTime,
  modifiedTime,
  noindex = false,
}: SEOHeadProps) => {
  const siteUrl = BUSINESS_INFO.website.url;
  // Append brand suffix only when it fits within the 60-char SEO limit.
  const suffix = ` | ${BUSINESS_INFO.name} Denver`;
  const fullTitle = title.includes(BUSINESS_INFO.name)
    ? title
    : (title.length + suffix.length <= 60 ? `${title}${suffix}` : title);
  
  // Normalize canonical URL - use provided or current path
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const fullCanonical = normalizeCanonical(canonical || currentPath);
  
  const fullOgImage = ogImage.startsWith("http") ? ogImage : `${siteUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonical} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      
      {/* Geo Tags for Local SEO */}
      <meta name="geo.region" content="US-CO" />
      <meta name="geo.placename" content="Denver" />
      <meta name="geo.position" content="39.7392;-104.9903" />
      <meta name="ICBM" content="39.7392, -104.9903" />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content={`${BUSINESS_INFO.name} Mobile Pet Grooming`} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:site" content={BUSINESS_INFO.social.twitter} />
      <meta name="twitter:creator" content={BUSINESS_INFO.social.twitter} />
      
      {/* Article Meta Tags */}
      {author && <meta name="author" content={author} />}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      
      {/* Additional SEO */}
      <meta name="theme-color" content="#00DC82" />
      <meta name="format-detection" content="telephone=no" />
    </Helmet>
  );
};

export default SEOHead;
